import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import { Users, FileText, Eye, DollarSign } from "lucide-react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import "./../../styles/member/statistical.css";
import { statisticsService, StatisticsData } from "../../services/statisticsService";

const Statistical: React.FC = () => {
  const [statisticsData, setStatisticsData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const data = await statisticsService.getDetailedStatistics();
        setStatisticsData(data);
      } catch (err) {
        setError("Không thể tải dữ liệu thống kê");
      } finally {
        setLoading(false);
      }
    };
    fetchStatistics();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="statistical-container">
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>Đang tải dữ liệu thống kê...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !statisticsData) {
    return (
      <Layout>
        <div className="statistical-container">
          <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
            <p>{error || "Không có dữ liệu thống kê"}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Biểu đồ đường
  const lineData = statisticsData.timeSeries.users.map((userData, index) => ({
    name: userData.month,
    users: userData.count,
    posts: statisticsData.timeSeries.posts[index]?.count || 0,
  }));

  // Biểu đồ tròn (phân bố danh mục)
  const pieData = statisticsData.categories.map((category, index) => ({
    name: category.name,
    value: category.count,
    color: ["#FF6B6B", "#4ECDC4", "#45B7D1", "#FFD93D", "#96CEB4", "#A66DD4"][index % 6],
  }));

  // Biểu đồ cột
  const barData = statisticsData.timeSeries.approvals.map((approvalData) => ({
    name: approvalData.month,
    Duyệt: approvalData.approved,
    "Từ chối": approvalData.rejected,
  }));

  const getGrowthClass = (growth: string) => {
    if (growth.startsWith("+")) return "success";
    if (growth.startsWith("-")) return "danger";
    return "neutral";
  };

  const formatGrowthText = (growth: string) => {
    if (growth === "+0%" || growth === "0%") return "Không thay đổi so với tháng trước";
    return `${growth} so với tháng trước`;
  };

  const COLORS = ["#4ECDC4", "#FF6B6B", "#FFD93D", "#45B7D1", "#96CEB4", "#A66DD4"];

  return (
    <Layout>
      <div className="statistical-container">
        <h2 className="page-title">Thống kê & Phân tích</h2>
        <p className="page-subtitle">Theo dõi hiệu suất và xu hướng của hệ thống</p>

        {/* Cards tổng quan */}
        <div className="statistical-cards">
          <div className="stat-card">
            <div className="icon"><Users size={22} /></div>
            <div>
              <p>Tổng người dùng</p>
              <h3>{statisticsData.overview.totalUsers.toLocaleString()}</h3>
              <span className={getGrowthClass(statisticsData.overview.userGrowth)}>
                {formatGrowthText(statisticsData.overview.userGrowth)}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="icon"><FileText size={22} /></div>
            <div>
              <p>Tổng bài viết</p>
              <h3>{statisticsData.overview.totalPosts.toLocaleString()}</h3>
              <span className={getGrowthClass(statisticsData.overview.postGrowth)}>
                {formatGrowthText(statisticsData.overview.postGrowth)}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="icon"><Eye size={22} /></div>
            <div>
              <p>Lượt xem</p>
              <h3>{statisticsData.overview.totalViews.toLocaleString()}</h3>
              <span className={getGrowthClass(statisticsData.overview.viewGrowth)}>
                {formatGrowthText(statisticsData.overview.viewGrowth)}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="icon"><DollarSign size={22} /></div>
            <div>
              <p>Doanh thu</p>
              <h3>{statisticsData.overview.totalRevenue.toLocaleString()} VND</h3>
              <span className={getGrowthClass(statisticsData.overview.revenueGrowth)}>
                {formatGrowthText(statisticsData.overview.revenueGrowth)}
              </span>
            </div>
          </div>
        </div>

        {/* Biểu đồ */}
        <div className="charts-grid">
          {/* Tăng trưởng */}
          <div className="chart-card">
            <h4>Tăng trưởng người dùng</h4>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={lineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="users" stroke="#007bff" />
                  <Line type="monotone" dataKey="posts" stroke="#1e5f00ff" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Biểu đồ tròn */}
          <div className="chart-card rounded-chart">
            <h4>Phân bố theo danh mục</h4>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={4}
                    cornerRadius={12} // Bo góc từng lát
                    label={({ name, percent }: any) => `${(percent * 100).toFixed(0)}% ${name}`}
                    labelLine={{ stroke: "#ccc", strokeDasharray: "3 3" }}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`slice-${index}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>

                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    content={({ active, payload, coordinate }) => {
                      if (active && payload && payload.length) {
                        const { name, value, color } = payload[0].payload;
                        return (
                          <div
                            style={{
                              position: "absolute",
                              left: coordinate?.x ?? 0,
                              top: coordinate?.y ?? 0,
                              transform: "translate(-50%, -120%)",
                              background: "#fff",
                              borderRadius: "12px",
                              border: "1px solid #eee",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              padding: "6px 10px",
                              pointerEvents: "none",
                              whiteSpace: "nowrap",
                              fontSize: "13px",
                              color: "#333",
                              fontWeight: 500,
                            }}
                          >
                            <span style={{ color }}>{name}</span>:{" "}
                            <strong>{value.toLocaleString()} bài viết</strong>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Thống kê gói dịch vụ (cải tiến) */}
          {/* Thống kê gói dịch vụ (thiết kế mới) */}
<div className="chart-card service-stats-card">
  <h4>Thống kê gói dịch vụ</h4>
  <div className="service-stats-grid">
    {statisticsData.packages.map((pkg, index) => (
      <div className="service-card11" key={index}>
        <div className="service-header">
          <span className="service-name">{pkg.name}</span>
          <span className="service-percentage">{pkg.percentage}%</span>
        </div>
        <div className="service-bar-wrapper">
          <div
            className="service-bar"
            style={{
              width: `${pkg.percentage}%`,
              backgroundColor: COLORS[index % COLORS.length],
            }}
          />
        </div>
        <p className="service-count">{pkg.count.toLocaleString()} người</p>
      </div>
    ))}
  </div>
</div>


          {/* Tỷ lệ duyệt bài */}
          <div className="chart-card1">
            <h4 style ={{marginBottom: 50}}>Tỷ lệ duyệt bài viết</h4>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height={250} >
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Duyệt" fill="#4ECDC4" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="Từ chối" fill="#FF6B6B" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top bài viết */}
        <div className="chart-card full-width">
          <h4>Bài viết có hiệu suất cao</h4>
          <ul className="top-posts">
            {statisticsData.topPosts.map((post) => (
              <li key={post.id}>
                <div>
                  <p className="post-title">{post.title}</p>
                  <span className="author">bởi {post.author}</span>
                </div>
                <span className="views">
                  {post.viewCount.toLocaleString()} lượt xem
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Statistical;
